import { useEffect, useRef, useState } from "react";

// Animates a numeric value from 0 → target over `durationMs`. Honors
// prefers-reduced-motion (returns the target immediately).
export function useCountUp(target: number, durationMs = 600): number {
  const [value, setValue] = useState<number>(target);
  const lastTarget = useRef<number>(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (lastTarget.current === target) return;
    const startVal = lastTarget.current;
    lastTarget.current = target;

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced || durationMs <= 0) {
      setValue(target);
      return;
    }

    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(startVal + (target - startVal) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  return value;
}
